import { RRule, Weekday } from 'rrule'
import { rruleDescription, RRuleWithOptions } from 'pages/calendar/utils/RRuleDesc'
import { TFunction } from 'i18next'

export interface RecurrenceOption {
  key: string
  label: string
  freq?: number
  interval?: number
}

export interface RRuleOptions {
  freq: number
  interval: number
  byweekday?: Weekday[]
  bymonthday?: number
  bymonth?: number
  until?: Date | number
}

export const RECURRENCE_OPTIONS: RecurrenceOption[] = [
  {
    key: 'never',
    label: 'calendar.recurrence.never',
  },
  {
    key: 'daily',
    label: 'calendar.recurrence.daily',
    freq: RRule.DAILY,
    interval: 1,
  },
  {
    key: 'weekly',
    label: 'calendar.recurrence.weekly',
    freq: RRule.WEEKLY,
    interval: 1,
  },
  {
    key: 'biweekly',
    label: 'calendar.recurrence.biweekly',
    freq: RRule.WEEKLY,
    interval: 2,
  },
  {
    key: 'monthly',
    label: 'calendar.recurrence.monthly',
    freq: RRule.MONTHLY,
    interval: 1,
  },
  {
    key: '3monthly',
    label: 'calendar.recurrence.every3months',
    freq: RRule.MONTHLY,
    interval: 3,
  },
  {
    key: '6monthly',
    label: 'calendar.recurrence.every6months',
    freq: RRule.MONTHLY,
    interval: 6,
  },
  {
    key: 'yearly',
    label: 'calendar.recurrence.yearly',
    freq: RRule.YEARLY,
    interval: 1,
  },
  {
    key: 'custom',
    label: 'calendar.recurrence.customRecurrence',
  },
]

/**
 * Creates RRule options based on recurrence key and start date
 */
export const createRecurrenceOptions = (
  key: string,
  startDate?: Date | string | null,
): RRuleOptions | null => {
  if (key === 'never') {
    return null
  }

  const option = RECURRENCE_OPTIONS.find((opt) => opt.key === key)
  if (!option || key === 'custom') {
    return null
  }

  const rruleOptions: RRuleOptions = {
    freq: option.freq!,
    interval: option.interval!,
  }

  const baseDate = startDate ? new Date(startDate) : new Date()

  if (option.freq === RRule.DAILY) {
    let until = new Date(baseDate)
    until.setMonth(baseDate.getMonth() + 1)
    if (until.getMonth() !== baseDate.getMonth() + 1) {
      until.setDate(0)
    }
    rruleOptions.until = until
  } else if (option.freq === RRule.WEEKLY) {
    let weekday = baseDate.getDay() - 1
    if (weekday === -1) {
      weekday = 6
    }
    rruleOptions.byweekday = [new Weekday(weekday)]
    rruleOptions.until = new Date(baseDate).setDate(baseDate.getDate() + 7 * 12)
  } else if (option.freq === RRule.MONTHLY) {
    rruleOptions.bymonthday = baseDate.getDate()
    rruleOptions.until = new Date(baseDate).setFullYear(baseDate.getFullYear() + 1)
  } else if (option.freq === RRule.YEARLY) {
    rruleOptions.bymonthday = baseDate.getDate()
    rruleOptions.bymonth = baseDate.getMonth() + 1
  }

  return rruleOptions
}

/**
 * Gets current recurrence description
 */
export const getCurrentRecurrenceDescription = (
  recurrenceRule: RRule | null | undefined,
  t: TFunction,
): string => {
  if (!recurrenceRule) {
    return t('calendar.recurrence.never')
  }

  return rruleDescription(recurrenceRule as RRuleWithOptions)
}
